import axios, { AxiosResponse } from "axios";
import qs from "qs";
import { CONFIG } from "../config";
import { DEFAULT_TEST_CASE } from "../constant";

export type ClientSignature = {
  code: number;
  msg: string;
  result: {
    banned: boolean;
    captcha: boolean;
    flag: boolean;
    ip: string;
    uuid: string;
    captchaUrl: string;
  };
};

export type LoginFn = (payload: LoginParam) => Promise<
  AxiosResponse<
    {
      code: number;
      data: {
        accessPage: {
          access_account: string;
          access_member: string;
          access_payment: string;
          access_report: string;
          access_stock: string;
        };
        hasPasscode: boolean;
        token: string;
      };
      token: string;
    },
    { code: number }
  >
>;

export type LoginParam = {
  password: string;
  username: string;
  clientSignature: string;
  captcha: string;
};

export type UpdateSeamlessParam = {
  agentId: string;
  hookProfile: string;
  hookTransfer: string;
};

export type WebhookHealthCheckParam = {
  username: string;
  caseId: string;
  title: string;
};

export type GetLogsOpts = {
  page?: number;
  limit?: number;
  betId?: string;
  startDate?: string;
  endDate?: string;
  status?: string[];
  typeLogs?: string[];
  event?: string[];
  errorCode?: number[];     
  dateField?: string;
};


export interface CreateMemberPayload {
  username: string;
  password: string;
  cur: string[];
  balance: Record<
    string,
    { initBalance: number; balance: number; status: boolean }
  >;
  isSeamless?: boolean;
  type?: string;
}

export interface CreateMemberResponse {
  code: number;
  msg: string;
}
export interface UpdatedByMember {
  _id: string;
  name: string;
}

export interface CurrencyLog {
  _id: string;
  baseCurrency: string;
  source: string;
  rates: Record<string, number>;
  status: string;
  changes: Array<{
    _id: string;
    currency: string;
    oldRate: number;
    newRate: number;
    changePercent: number;
  }>;
  approvedBy: UpdatedByMember,
  updatedBy: UpdatedByMember;
  requestedAt: string;
  updatedAt: string;
  createdAt: string;
}

export interface GetCurrenciesLogResponse {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  logs: CurrencyLog[];

}

export type GetHookLogOptionParams = {
  startDate?: string;
  endDate?: string;
  dateField?: string;
};

export type GetHookLogOptionResponse = {
  typeLogs: string[];
  event: string[];
  errorCode: number[];
};

export type ExportWinLoseOpts = {
  startDate: string;
  endDate: string;
  username?: string;
  sorting?: WinLoseSorting;
};

export type WinLoseSorting = Partial<
  Record<"tickets" | "betAmount" | "winLose", 1 | -1>
>;

const service = {
  clientSignature: async () => {
    const res = await axios.get<ClientSignature>(
      `${CONFIG.API_URL}/a/m/clientSignature`
    );
    return res.data;
  },
  login: (({ password, username, clientSignature, captcha }) => {
    return axios.post(`${CONFIG.API_URL}/a/m/authen`, {
      password,
      username,
      clientSignature,
      captcha,
    });
  }) as LoginFn,

  getProfile: async () => {
    const res = await axios.get(`${CONFIG.API_URL}/a/m/getProfile`);

    return res.data?.data;
  },

  createKey: () => {
    return axios.post(`${CONFIG.API_URL}/a/ws/api-key`);
  },

  getLogs: async (opts: GetLogsOpts) => {
    const {
      page = 1,
      limit = 10,
      betId,
      startDate,
      endDate,
      status,
      typeLogs,
      event,
      errorCode,
      dateField = "createDate",
    } = opts;

    const params: Record<string, any> = { page, limit, dateField };
    if (betId) params.betId = betId;
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (status && status.length) params.status = status;
    if (typeLogs && typeLogs.length) params.typeLogs = typeLogs;
    if (event && event.length) params.event = event;
    if (errorCode?.length) params.errorCode = errorCode;

    const res = await axios.get(`${CONFIG.API_URL}/a/ws/getLogHook`, {
      params,
    });

    return res.data?.data;
  },

  getSeamless: async () => {
    const res = await axios.get(`${CONFIG.API_URL}/a/ws/getSeamless`);

    if (
      !res.data?.data?.testCases ||
      res.data?.data?.testCases?.length !== DEFAULT_TEST_CASE.length
    ) {
      return {
        ...res.data?.data,
        testCases: DEFAULT_TEST_CASE,
      };
    }
    return res.data?.data;
  },

  updateSeamless: async (payload: UpdateSeamlessParam) => {
    const res = await axios.post(
      `${CONFIG.API_URL}/a/ws/updateSeamless`,
      payload
    );

    return res.data;
  },

  webhookHealthCheck: async (payload: WebhookHealthCheckParam) => {
    const res = await axios.post(
      `${CONFIG.API_URL}/a/ws/webhook-health-check`,
      payload
    );

    return res.data;
  },

  getOverview: async ({
    startDate = "",
    endDate = "",
  }: {
    startDate: string;
    endDate: string;
  }) => {
    const res = await axios.get(
      `${CONFIG.API_URL}/report/overview?startDate=${startDate}&endDate=${endDate}`
    );

    return res.data;
  },

  getWinLose: async ({
    startDate = "",
    endDate = "",
    username = "",
    sorting,
  }: {
    startDate: string;
    endDate: string;
    username?: string;
    sorting?: WinLoseSorting;
  }) => {
    const queryString = qs.stringify({
      startDate,
      endDate,
      username,
      ...(sorting ? { sorting } : {}),
    });

    const res = await axios.get(
      `${CONFIG.API_URL}/report/winLose?${queryString}`
    );

    return res.data;
  },

  exportWinLose: async ({
    startDate,
    endDate,
    username,
    sorting,
  }: ExportWinLoseOpts): Promise<Blob> => {
    const { tickets, ...otherSorting } = sorting || {};
    const exportSorting =
      tickets === undefined
        ? sorting
        : { ...otherSorting, count: tickets };
    const queryString = qs.stringify({
      startDate,
      endDate,
      ...(username ? { username } : {}),
      ...(exportSorting ? { sorting: exportSorting } : {}),
    });

    const res = await axios.get<Blob>(
      `${CONFIG.API_URL}/report/new-winLose/export?${queryString}`,
      {
        responseType: "blob",
      }
    );

    return res.data;
  },

  getDownline: async ({
    parentFocusId,
    focusId,
  }: {
    parentFocusId: string;
    focusId: string;
  }) => {
    const res = await axios.get(
      `${CONFIG.API_URL}/a/m/donwline?parentFocusId=${parentFocusId}&focusId=${focusId}`
    );

    return res.data;
  },

  getTicket: async ({
    username,
    betId,
    startDate,
    endDate,
    status = [],
    page = 1,
    limit = 50,
    dateField = "createDate",
    isAbnormal = undefined,
    sportType,
  }: {
    username?: string;
    betId: string;
    startDate?: string;
    endDate?: string;
    status?: string[];
    page?: number;
    limit?: number;
    dateField?: string;
    isAbnormal?: boolean;
    sportType?: string;
  }) => {
    const queryString = qs.stringify({
      startDate,
      endDate,
      username,
      betId,
      status,
      page,
      limit,
      dateField,
      isAbnormal,
      sportType,
    });
    const res = await axios.get(
      `${CONFIG.API_URL}/report/ticket?${queryString}`
    );

    return res.data;
  },
  manualStatus: async (payload: {
    event: string;
    betId: string;
    matches?: { result: string }[];
  }) => {
    return axios.post(`${CONFIG.API_URL}/a/ws/manual-status`, payload);
  },

  resendTransfer: async (id: string) => {
    const res = await axios.post(`${CONFIG.API_URL}/a/ws/resend-transfer`, {
      id: id,
    });

    return res.data;
  },
  getCurrencies: async () => {
    const res = await axios.get(
      `${CONFIG.API_URL}/a/currency/getCurrecy`
    );
    return res.data;
  },

  checkCurrencies: async () => {
    const res = await axios.get(
      `${CONFIG.API_URL}/a/currency/check`
    );
    return res.data;
  },

  updateCurrencies: async (
    rates: Record<string, number>
  ): Promise<{ rates: string }> => {
    const res = await axios.post(
      `${CONFIG.API_URL}/a/currency/update`,
      { base: "THB", rates }
    );
    return res.data;
  },

  getCurrenciesLog: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<GetCurrenciesLogResponse> => {
    const { page = 1, limit = 10 } = params || {};
    const res = await axios.get<GetCurrenciesLogResponse>(
      `${CONFIG.API_URL}/a/currency/logs`,
      {
        params: { page, limit },
      }
    );
    return res.data;
  },
  approveCurrencyLog: async (
    logId: string
  ): Promise<{ rates: string }> => {
    const res = await axios.post(
      `${CONFIG.API_URL}/a/currency/approve`,
      { logId }
    );
    return res.data;
  },


  createMember: async (
    payload: CreateMemberPayload
  ): Promise<CreateMemberResponse> => {
    const res = await axios.post<CreateMemberResponse>(
      `${CONFIG.API_URL}/a/m/createMembers`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return res.data;
  },

  getSportState: async ({
    startDate = "",
    endDate = "",
    parentId = ""
  }: {
    startDate: string;
    endDate: string;
    parentId: string
  }) => {
    const queryString = qs.stringify({
      startDate,
      endDate,
      parentId
    });

    const res = await axios.get(
      `${CONFIG.API_URL}/report/sport-state?${queryString}`
    );

    return res.data;
  },

  getHookLogOption: async (
    params?: GetHookLogOptionParams
  ): Promise<GetHookLogOptionResponse> => {
    const res = await axios.get(
      `${CONFIG.API_URL}/a/ws/getHookLogOption`,
      { params }
    );
    return res.data?.data as GetHookLogOptionResponse;
  },

};

export default service;
